import React, { useEffect, useRef } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import type { AutosaveState } from '@components/AutosaveStatus';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useGetClaimByIdQuery } from '../../store/api';

import type { CMS1500FormData } from './ClaimCMS1500Form';
import { ClaimCMS1500Form } from './ClaimCMS1500Form';
import { CreatingClaim } from './CreatingClaim';

interface ClaimEditProps {
  readOnly?: boolean;
  onAutosaveStateChange?: (
    status: AutosaveState,
    isDirty: boolean,
    lastSavedAt: Date | null,
    onRetry: () => void,
  ) => void;
  onFormMethodsReady?: (methods: UseFormReturn<CMS1500FormData>) => void;
  onFieldChange?: (fieldName: string) => void;
}

export const ClaimEdit: React.FC<ClaimEditProps> = ({
  readOnly = false,
  onAutosaveStateChange,
  onFormMethodsReady,
  onFieldChange,
}) => {
  const { claimId } = useParams<{ claimId: string }>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { billing: billingMetrics } = useEventTracking();
  const hasTrackedView = useRef(false);

  const {
    data: claim,
    isLoading,
    error,
    refetch,
  } = useGetClaimByIdQuery(
    { claimId: claimId ?? '' },
    {
      skip: !claimId,
      // Disable built-in polling - we'll handle it manually
      pollingInterval: 0,
    },
  );

  // Manual polling with cleanup
  useEffect(() => {
    const status = claim?.status;

    if (status === 'processing') {
      intervalRef.current = setInterval(() => {
        refetch();
      }, 2000);
    } else if (claim && status) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [claim, claim?.status, refetch]);

  // Track claim view once the claim is loaded
  useEffect(() => {
    if (claim && claimId && !hasTrackedView.current) {
      hasTrackedView.current = true;
      billingMetrics.viewClaim(claimId, {
        claim_status: claim.status,
        is_submitted: ['submitted', 'acknowledged', 'accepted', 'rejected'].includes(claim.status ?? ''),
      });
    }
  }, [claim, claimId, billingMetrics]);

  if (!claimId) {
    return <Alert severity="error">No claim ID provided</Alert>;
  }

  if (isLoading && !claim) {
    // Simple loader for initial load - no text
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load claim details. Please try again.</Alert>;
  }

  if (!claim) {
    return null;
  }

  // Show processing state with message when claim is being generated (skip in read-only mode)
  if (claim.status === 'processing' && !readOnly) {
    return <CreatingClaim />;
  }

  // Show the claim data with CMS-1500 form
  return (
    <ClaimCMS1500Form
      claim={claim}
      readOnly={readOnly}
      onAutosaveStateChange={onAutosaveStateChange}
      onFormMethodsReady={onFormMethodsReady}
      onFieldChange={onFieldChange}
    />
  );
};
